import { useMemo, useState } from "react"
import { toast } from "react-toastify"
import {
  CheckIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline"
import { Card, Chip } from "~/ui-rtk/components/ui/common"
import { Button, Input } from "~/ui-rtk/components/ui/controls"
import { H3 } from "~/ui-rtk/components/ui/typography"
import {
  formatTagLabel,
  type TagCategory,
} from "~/ui-rtk/types/structured-tags"

/** Short, human meta-line label for each category value type. */
const VALUE_TYPE_LABELS: Record<string, string> = {
  user_generated: "open list",
  predefined: "predefined",
  mixed: "mixed",
  free_text: "free text",
}

/**
 * One category and every value it allows.
 *
 * Values render as removable chips rather than a table: a category can hold
 * 100+ values, and seeing them together in one dense block is what makes
 * near-duplicates (`mold` next to `mold_exposure`) visible at a glance.
 */

type CategoryCardProps = {
  category: TagCategory
  onRename: (label: string) => Promise<void>
  onUpdateDescription: (description: string) => Promise<void>
  onUpdateOptionDescription: (
    optionId: string,
    description: string,
  ) => Promise<void>
  onAddOption: (label: string) => Promise<unknown>
  onRequestDeleteOption: (optionId: string, label: string) => void
  onRequestDeleteCategory: () => void
}

/** Values above this many get a filter box; below it, scanning is fine. */
const FILTER_THRESHOLD = 12

export default function CategoryCard({
  category,
  onRename,
  onUpdateDescription,
  onUpdateOptionDescription,
  onAddOption,
  onRequestDeleteOption,
  onRequestDeleteCategory,
}: CategoryCardProps) {
  const [isRenaming, setIsRenaming] = useState(false)
  const [draftLabel, setDraftLabel] = useState(category.label)
  const [newOption, setNewOption] = useState("")
  const [isAdding, setIsAdding] = useState(false)
  const [filter, setFilter] = useState("")
  const [isEditingDescription, setIsEditingDescription] = useState(false)
  const [draftDescription, setDraftDescription] = useState(
    category.description ?? "",
  )
  // The option whose description is being edited in the row below the chips.
  const [editingOption, setEditingOption] = useState<{
    id: string
    label: string
    draft: string
  } | null>(null)
  const [isSavingDescription, setIsSavingDescription] = useState(false)

  // Free-text categories have no curated value pool — values are typed while
  // tagging ads, so this card only reviews/retires them (no "Add a value").
  const isFreeText = category.valueType === "free_text"
  const options = category.options ?? []

  const visibleOptions = useMemo(() => {
    const needle = filter.trim().toLowerCase()
    if (!needle) return options
    return options.filter(
      option =>
        option.label.toLowerCase().includes(needle) ||
        option.value.toLowerCase().includes(needle),
    )
  }, [options, filter])

  // Distinguishes "this category has nothing yet" from "your filter excluded
  // everything" — the two look identical otherwise and one is alarming.
  const emptyMessage = (() => {
    if (options.length === 0) {
      return isFreeText
        ? "Free text — values are typed while tagging ads and collect here for review."
        : "No values yet. Add the first one below."
    }
    if (visibleOptions.length === 0) return `No values match "${filter}".`
    return null
  })()

  const commitRename = async () => {
    const label = draftLabel.trim()
    if (!label || label === category.label) {
      setIsRenaming(false)
      setDraftLabel(category.label)
      return
    }
    try {
      await onRename(label)
      setIsRenaming(false)
      toast.success("Category renamed")
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Could not rename the category",
      )
    }
  }

  const commitDescription = async () => {
    if (isSavingDescription) return
    const description = draftDescription.trim()
    if (description === (category.description ?? "")) {
      setIsEditingDescription(false)
      return
    }
    setIsSavingDescription(true)
    try {
      await onUpdateDescription(description)
      setIsEditingDescription(false)
      toast.success("Description saved")
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Could not save the description",
      )
    } finally {
      setIsSavingDescription(false)
    }
  }

  const commitOptionDescription = async () => {
    if (!editingOption || isSavingDescription) return
    setIsSavingDescription(true)
    try {
      await onUpdateOptionDescription(
        editingOption.id,
        editingOption.draft.trim(),
      )
      setEditingOption(null)
      toast.success("Description saved")
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Could not save the description",
      )
    } finally {
      setIsSavingDescription(false)
    }
  }

  const commitAddOption = async () => {
    // The button disables itself while adding, but the Enter key path doesn't.
    if (isAdding) return
    const label = newOption.trim()
    if (!label) return
    setIsAdding(true)
    try {
      await onAddOption(label)
      setNewOption("")
      toast.success(`Added "${label}"`)
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Could not add the value",
      )
    } finally {
      setIsAdding(false)
    }
  }

  return (
    <Card className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {isRenaming ? (
            <div className="flex items-center gap-2">
              <Input
                value={draftLabel}
                onChange={e => setDraftLabel(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") commitRename()
                  if (e.key === "Escape") {
                    setIsRenaming(false)
                    setDraftLabel(category.label)
                  }
                }}
              />
              <button
                type="button"
                aria-label="Save category name"
                className="text-system-gray-300 hover:text-white"
                onClick={commitRename}
              >
                <CheckIcon className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <H3 className="text-white font-bold text-base">
                {formatTagLabel(category.label)}
              </H3>
              <button
                type="button"
                aria-label={`Rename ${category.label}`}
                className="text-system-gray-500 hover:text-white"
                onClick={() => {
                  // Re-seed from the current prop: the label may have changed
                  // since mount (another tab, an agent via tags_manage), and a
                  // stale draft would silently revert that rename on save.
                  setDraftLabel(category.label)
                  setIsRenaming(true)
                }}
              >
                <PencilIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-system-gray-500">
            <code>{category.slug}</code>
            <span>·</span>
            <span>
              {options.length} {options.length === 1 ? "value" : "values"}
            </span>
            {category.allowMultiple && (
              <>
                <span>·</span>
                <span>multi-select</span>
              </>
            )}
            {VALUE_TYPE_LABELS[category.valueType] && (
              <>
                <span>·</span>
                <span>{VALUE_TYPE_LABELS[category.valueType]}</span>
              </>
            )}
            {category.isSystem && (
              <>
                <span>·</span>
                <span className="text-system-sea-500">system</span>
              </>
            )}
          </div>

          {/* Category description — guides both humans and the AI tag
              suggester, so it's editable on system categories too. */}
          {isEditingDescription ? (
            <div className="flex items-center gap-2 mt-1.5">
              <Input
                value={draftDescription}
                placeholder="What this category captures (guides the AI tag suggester)"
                onChange={e => setDraftDescription(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") commitDescription()
                  if (e.key === "Escape") {
                    setIsEditingDescription(false)
                    setDraftDescription(category.description ?? "")
                  }
                }}
              />
              <button
                type="button"
                aria-label="Save category description"
                className="text-system-gray-300 hover:text-white"
                onClick={commitDescription}
              >
                <CheckIcon className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="mt-1.5 text-left text-sm text-system-gray-400 hover:text-system-gray-200"
              onClick={() => {
                setDraftDescription(category.description ?? "")
                setIsEditingDescription(true)
              }}
            >
              {category.description ?? (
                <span className="italic text-system-gray-600">
                  Add a description to guide the AI tag suggester
                </span>
              )}
            </button>
          )}
        </div>

        {/* System defaults are deletable too: a brand that never tags
            "Length" removes it here, and the AI tagger stops suggesting it. */}
        <button
          type="button"
          aria-label={`Delete ${category.label}`}
          className="text-system-gray-500 hover:text-system-error-500 shrink-0"
          onClick={onRequestDeleteCategory}
        >
          <TrashIcon className="w-4 h-4" />
        </button>
      </div>

      {options.length > FILTER_THRESHOLD && (
        <Input
          value={filter}
          placeholder={`Filter ${options.length} values`}
          onChange={e => setFilter(e.target.value)}
        />
      )}

      {emptyMessage ? (
        <p className="text-sm text-system-gray-500">{emptyMessage}</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {visibleOptions.map(option => (
            <span
              key={option.id}
              // Click a value to describe it (hover shows the description).
              // A dotted underline marks values that already have one.
              title={option.description ?? "Click to add a description"}
              className="cursor-pointer"
              onClick={e => {
                // The chip's remove × bubbles here; deleting shouldn't also
                // open the description editor.
                if ((e.target as HTMLElement).closest("button")) return
                setEditingOption({
                  id: option.id,
                  label: option.label,
                  draft: option.description ?? "",
                })
              }}
            >
              <Chip
                className={`bg-system-gray-800 text-system-gray-200 !py-0.5 !px-2 text-xs ${
                  option.description
                    ? "underline decoration-dotted underline-offset-2"
                    : ""
                }`}
                // System-seeded options mirror the category-level rule: the
                // server rejects deleting them, so don't offer the affordance.
                onRemove={
                  option.isSystem
                    ? undefined
                    : () => onRequestDeleteOption(option.id, option.label)
                }
              >
                {formatTagLabel(option.label)}
              </Chip>
            </span>
          ))}
        </div>
      )}

      {editingOption && (
        <div className="flex items-end gap-2 rounded-lg border border-system-gray-700 p-2">
          <div className="flex-1">
            <p className="text-xs text-system-gray-400 mb-1">
              When does <strong>{formatTagLabel(editingOption.label)}</strong>{" "}
              apply? Guides the AI tag suggester.
            </p>
            <Input
              value={editingOption.draft}
              placeholder="e.g. The founder appears on camera speaking to the viewer"
              onChange={e =>
                setEditingOption({ ...editingOption, draft: e.target.value })
              }
              onKeyDown={e => {
                if (e.key === "Enter") commitOptionDescription()
                if (e.key === "Escape") setEditingOption(null)
              }}
            />
          </div>
          <Button
            variant={{ variant: "solid", color: "white" }}
            size="sm"
            type="button"
            isLoading={isSavingDescription}
            disabled={isSavingDescription}
            onClick={commitOptionDescription}
          >
            Save
          </Button>
          <Button
            variant={{ variant: "outlined" }}
            size="sm"
            type="button"
            disabled={isSavingDescription}
            onClick={() => setEditingOption(null)}
          >
            Cancel
          </Button>
        </div>
      )}

      {!isFreeText && (
        <div className="flex items-end gap-2">
          <Input
            value={newOption}
            placeholder="Add a value"
            onChange={e => setNewOption(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") commitAddOption()
            }}
          />
          <Button
            variant={{ variant: "solid", color: "white" }}
            size="sm"
            type="button"
            isLoading={isAdding}
            disabled={!newOption.trim() || isAdding}
            onClick={commitAddOption}
          >
            <PlusIcon className="w-4 h-4 mr-1" />
            Add
          </Button>
        </div>
      )}
    </Card>
  )
}
